import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Chip,
    Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faTimesCircle,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';
import SearchableSelect from '../../common/SearchableSelect';

/**
 * Job Card Reconciliation - Compare invoiced job cards vs owner_trans JIN records (requires jobcards.view)
 */
const JobCardReconciliation = () => {
    const { token } = useAuth();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        owner_code: '',
        plate_no: ''
    });
    const [ownerOptions, setOwnerOptions] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.owner_code) params.append('owner_code', filters.owner_code);
            if (filters.plate_no) params.append('plate_no', filters.plate_no);
            params.append('limit', '500');

            const url = `${rootUrl}/api/job-cards/reconciliation?${params}`;
            const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

            if (res.data?.success) {
                setData(res.data.data || []);
                setFetchError(null);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error('Reconciliation fetch error:', err);
            setData([]);
            setFetchError(err.response?.data?.message || err.message || 'Failed to load reconciliation data');
        } finally {
            setLoading(false);
        }
    }, [token, filters.date_from, filters.date_to, filters.owner_code, filters.plate_no]);

    useEffect(() => {
        const loadOwners = async () => {
            try {
                const res = await axios.get(`${rootUrl}/owners`, { headers: { Authorization: `Bearer ${token}` } });
                const arr = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setOwnerOptions(arr.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` })));
            } catch (err) {
                console.error('Error loading owners:', err);
            }
        };
        loadOwners();
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const matchStatusChip = (status) => {
        if (status === 'ok') return <Chip color="green" size="sm" icon={<FontAwesomeIcon icon={faCheckCircle} />}>Match</Chip>;
        if (status === 'no_jin') return <Chip color="amber" size="sm" icon={<FontAwesomeIcon icon={faExclamationTriangle} />}>No JIN</Chip>;
        return <Chip color="red" size="sm" icon={<FontAwesomeIcon icon={faTimesCircle} />}>Mismatch</Chip>;
    };

    const formatAmount = (v) => (v != null ? new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(v) : '-');
    const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-');
    const buildOwnerTransactionPath = (ownerCode, transId) => (
        ownerCode && transId ? `/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}` : null
    );
    const getDocumentNumber = (value) => (
        String(value || '').split(':').pop() || value || '-'
    );

    const mismatchCount = data.filter(r => r.match_status !== 'ok').length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Job Card Reconciliation</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">Compare invoiced job cards with related owner transactions (JIN). Shows mismatches and missing JIN records.</p>
                </div>
            </div>
            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
            {fetchError && (
                <Alert color="red" className="mb-4" onClose={() => setFetchError(null)}>
                    {fetchError}
                </Alert>
            )}
            <Card className="mb-4 shadow-none rounded-sm bg-white">
                <CardBody>
                    <div className="flex flex-wrap gap-4 items-end">
                        <div>
                            <Typography variant="small" className="mb-1">Date From</Typography>
                            <Input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                                className="min-w-[140px]"
                            />
                        </div>
                        <div>
                            <Typography variant="small" className="mb-1">Date To</Typography>
                            <Input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                                className="min-w-[140px]"
                            />
                        </div>
                        <div className="min-w-[200px]">
                            <Typography variant="small" className="mb-1">Owner</Typography>
                            <SearchableSelect
                                options={ownerOptions}
                                value={filters.owner_code || null}
                                onChange={(val) => setFilters(f => ({ ...f, owner_code: val || '' }))}
                                placeholder="All owners"
                            />
                        </div>
                        <div>
                            <Typography variant="small" className="mb-1">Plate No</Typography>
                            <Input
                                placeholder="e.g. KCA 123A"
                                value={filters.plate_no}
                                onChange={(e) => setFilters(f => ({ ...f, plate_no: e.target.value.trim() }))}
                            />
                        </div>
                        <Button
                            size="sm"
                            onClick={fetchData}
                            disabled={loading}
                            className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white shadow-none rounded-sm"
                        >
                            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSync} />}
                            Refresh
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <Card className="shadow-none rounded-sm bg-white overflow-hidden">
                <CardBody className="p-0">
                    <div className="px-6 pt-6 pb-4 flex flex-row flex-wrap items-center justify-between gap-4">
                        <Typography variant="h6" className="font-semibold text-[#2a2e30]">Invoiced Job Cards vs Owner Transactions</Typography>
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2 text-sm">
                                <Chip color="green" size="sm" icon={<FontAwesomeIcon icon={faCheckCircle} />} className="normal-case">Match</Chip>
                                <Chip color="amber" size="sm" icon={<FontAwesomeIcon icon={faExclamationTriangle} />} className="normal-case">No JIN</Chip>
                                <Chip color="red" size="sm" icon={<FontAwesomeIcon icon={faTimesCircle} />} className="normal-case">Mismatch</Chip>
                            </div>
                            <span className="text-sm text-gray-500">
                                {data.length} record(s){mismatchCount > 0 ? ` · ${mismatchCount} need review` : ''}
                            </span>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        </div>
                    ) : (
                        <table className="w-full min-w-[900px]">
                            <thead>
                                <tr className="border-b bg-gray-50">
                                    <th className="text-left py-2 px-2">Status</th>
                                    <th className="text-left py-2 px-2">Job Card</th>
                                    <th className="text-left py-2 px-2">Plate</th>
                                    <th className="text-left py-2 px-2">JC Owner</th>
                                    <th className="text-right py-2 px-2">JC Amount</th>
                                    <th className="text-left py-2 px-2">JC Inv Date</th>
                                    <th className="text-left py-2 px-2">Trans Doc No</th>
                                    <th className="text-left py-2 px-2">Trans Owner</th>
                                    <th className="text-right py-2 px-2">Trans Amount</th>
                                    <th className="text-left py-2 px-2">Trans Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, i) => (
                                    <tr
                                        key={row.job_card?.id || i}
                                        className={`border-b hover:bg-gray-50 cursor-pointer ${row.match_status === 'mismatch' ? 'bg-red-50' : row.match_status === 'no_jin' ? 'bg-amber-50' : ''}`}
                                        onClick={() => row.job_card?.id && navigate(`/job-cards/${row.job_card.id}`)}
                                    >
                                        <td className="py-2 px-2">{matchStatusChip(row.match_status)}</td>
                                        <td className="py-2 px-2 font-mono">{row.job_card?.job_card_no || '-'}</td>
                                        <td className="py-2 px-2">
                                            {row.job_card?.plate_no ? (
                                                <Link
                                                    to={`/cars/${encodeURIComponent(row.job_card.plate_no)}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                                >
                                                    {row.job_card.plate_no}
                                                </Link>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td className="py-2 px-2">{row.job_card?.owner_code || '-'}</td>
                                        <td className="py-2 px-2 text-right">{formatAmount(row.job_card?.total_cost)}</td>
                                        <td className="py-2 px-2">{formatDate(row.job_card?.invoice_date)}</td>
                                        <td className="py-2 px-2 font-mono">
                                            {buildOwnerTransactionPath(row.owner_trans?.owner_code, row.owner_trans?.id) ? (
                                                <Link
                                                    to={buildOwnerTransactionPath(row.owner_trans?.owner_code, row.owner_trans?.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-[#FF5733] hover:text-[#e64520] hover:underline font-semibold"
                                                >
                                                    {getDocumentNumber(row.owner_trans?.id || row.owner_trans?.doc_no)}
                                                </Link>
                                            ) : (
                                                getDocumentNumber(row.owner_trans?.doc_no)
                                            )}
                                        </td>
                                        <td className="py-2 px-2">{row.owner_trans?.owner_code || '-'}</td>
                                        <td className="py-2 px-2 text-right">{formatAmount(row.owner_trans?.amount)}</td>
                                        <td className="py-2 px-2">{formatDate(row.owner_trans?.date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {!loading && data.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            <FontAwesomeIcon icon={faSearch} className="mb-2" size="2x" />
                            <p>No invoiced job cards found for the selected filters.</p>
                        </div>
                    )}
                    </div>
                </CardBody>
            </Card>
            </div>
        </div>
    );
};

export default JobCardReconciliation;
